import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AsOfFilter, download, EmptyState, ExportButton, LoadingOverlay, money, REPORTS, sheetHeading, todayIso } from "./common";
import { sideBySideRows, StatementSide, StatementSides } from "./Statement";

/** Section → its groups → (when detailed) the ledgers in each group. */
const sectionRows = (sections, detailed) =>
  sections.flatMap((section) => [
    { label: section.label, amount: section.total, level: 0, strong: true },
    ...section.groups.flatMap((group) => [
      { label: group.label, amount: group.total, level: 1 },
      ...(detailed ? group.lines.map((ledger) => ({ label: ledger.name, amount: ledger.amount, level: 2 })) : []),
    ]),
  ]);

const BalanceSheet = () => {
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [detailed, setDetailed] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!asOfDate) {
      toast.error("Select a date");
      return;
    }
    setLoading(true);
    try {
      setSheet(await api.get(`${REPORTS}/balance-sheet`, { asOfDate }));
    } catch (error) {
      toast.error(error.message || "Failed to load balance sheet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sides = (isDetailed) => ({
    liabilities: { title: "Liabilities", rows: sectionRows(sheet.liabilities, isDetailed), total: sheet.totalLiabilities },
    assets: { title: "Assets", rows: sectionRows(sheet.assets, isDetailed), total: sheet.totalAssets },
  });

  const exportExcel = () => {
    const sheetFor = (isDetailed) => {
      const s = sides(isDetailed);
      return [...sheetHeading("Balance Sheet", `As on ${sheet.asOfDate}`), ...sideBySideRows(s.liabilities, s.assets)];
    };
    download(`Balance Sheet ${sheet.asOfDate}`, [
      { name: "Balance Sheet", rows: sheetFor(false), widths: [40, 16, 4, 40, 16] },
      { name: "Ledger-wise", rows: sheetFor(true), widths: [44, 16, 4, 44, 16] },
    ]);
  };

  const current = sheet ? sides(detailed) : null;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        What the business owns and owes on a date. Closing stock comes from inventory valuation; Profit & Loss A/c is the profit accumulated since
        the books began.
      </div>
      <div className="filters-bar">
        <AsOfFilter value={asOfDate} onChange={setAsOfDate} />
        <select value={detailed ? "detailed" : "summary"} onChange={(e) => setDetailed(e.target.value === "detailed")} aria-label="Detail level">
          <option value="summary">Group summary</option>
          <option value="detailed">Ledger-wise detail</option>
        </select>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!sheet} />
      </div>

      {!sheet ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          {!sheet.balanced ? (
            <div className="page-subtitle" style={{ marginTop: "12px", color: "var(--color-danger)" }}>
              Assets and liabilities differ by ₹{money(sheet.difference)}.
            </div>
          ) : null}
          {Math.abs(Number(sheet.openingBalanceDifference)) > 0.005 ? (
            <div className="page-subtitle" style={{ marginTop: "12px", color: "var(--color-warning)" }}>
              Opening balances don't tally: debit and credit openings differ by ₹{money(Math.abs(sheet.openingBalanceDifference))}. Correct the ledger
              opening balances (or opening stock in Accounting Settings) to clear the "Difference in opening balances" line.
            </div>
          ) : null}
          <StatementSides>
            <StatementSide {...current.liabilities} />
            <StatementSide {...current.assets} />
          </StatementSides>
        </>
      )}
    </div>
  );
};

export default BalanceSheet;
